import format from 'date-fns/format';
import { isValid } from 'date-fns';
import { Hours, KoddiLocaleKey } from './LocaleProvider.types';
import { KODDI_LOCALE_TO_DATEFNS } from './LocaleProvider.constants';

/**
 * Converts a date string to a Date object without timezone offset
 * @param dateString - The date string to convert.
 * string must be in the format of YYYY-MM-DD
 * @returns The Date object.
 */
export const convertDateStringToDateObject = (
    dateString: string,
    hourOffset?: Hours
): Date => {
    const dateTimeRegex = /^\d{4}-\d{1,2}-\d{1,2}$/;
    const isValidString = dateTimeRegex.test(dateString);
    if (!isValidString) {
        // eslint-disable-next-line no-console
        console.warn(
            'Invalid date string passed to convertDateStringToDateObject. Expected format: YYYY-MM-DD. Date object maybe returned with browser timezone offset. ',
            { value: dateString }
        );
        return new Date(dateString);
    }
    const [year, month, day] = dateString.split('-').map(Number);
    const dateObject = new Date(year, month - 1, day);
    if (hourOffset) {
        dateObject.setHours(hourOffset);
    }
    return dateObject;
};

export function isValidKoddiLocale(
    possibleLocale: string,
    errorMessage?: string
): boolean {
    if (!possibleLocale) return false;
    const localeIsValid =
        Object.keys(KODDI_LOCALE_TO_DATEFNS).findIndex(
            (validLocale) => validLocale === possibleLocale
        ) > -1;
    if (localeIsValid) return true;

    // eslint-disable-next-line no-console
    console.error(
        errorMessage ||
            `LocaleProvider: The locale "${possibleLocale}" is not supported by Koddi. As a result the locale will be set to "en-US" to prevent the app from crashing.`
    );
    return false;
}

/**
 * A helper function for formatting dates for a specific locale.
 * By default dates of formatted using the `P` (Long localized date) format string such as MM/DD/YYYY or YYYY-MM-DD.
 */
export function formatDate(
    date: string | Date,
    possibleLocale: KoddiLocaleKey,
    dateFormat?: string
): string {
    const localeIsValid = isValidKoddiLocale(
        possibleLocale,
        `formatDate: The locale ${possibleLocale} is not supported by Koddi. The date you are trying to format will be formatted using "en-US".`
    );
    const dateFormatRegex = new RegExp('\\d{4}/\\d{2}/\\d{2}');

    const dateToFormat = date;

    if (
        typeof dateToFormat === 'string' &&
        dateFormatRegex.test(dateToFormat)
    ) {
        dateToFormat.replaceAll('/', '-');
    }

    const dt =
        typeof dateToFormat === 'string'
            ? convertDateStringToDateObject(dateToFormat)
            : dateToFormat;

    // Check if the date is valid before formatting
    if (!isValid(dt)) {
        return '';
    }

    const locale = localeIsValid ? possibleLocale : 'en-US';
    return format(dt, dateFormat || 'P', {
        locale: KODDI_LOCALE_TO_DATEFNS[locale],
    });
}
